import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, FlaskConical, Trash2, RotateCcw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ExpiredRow {
  id: string;
  display_name: string | null;
  demo_expires_at: string | null;
  tier: string | null;
  kyc_provider: string | null;
}

const DemoAccountsPanel = () => {
  const [rows, setRows] = useState<ExpiredRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("profiles")
      .select("id, display_name, demo_expires_at, tier, kyc_provider")
      .eq("demo_account", true)
      .lt("demo_expires_at", new Date().toISOString())
      .order("demo_expires_at", { ascending: true })
      .limit(200);
    if (error) toast.error(error.message);
    setRows((data ?? []) as ExpiredRow[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const run = async (mode: "soft" | "hard") => {
    if (rows.length === 0) {
      toast.info("Nothing to clean up");
      return;
    }
    if (mode === "hard" && !confirm(
      `Permanently DELETE ${rows.length} expired demo accounts? This cannot be undone.`
    )) return;

    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-purge-demo-accounts", {
        body: { mode, limit: 1000 },
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error ?? "purge_failed");
      toast.success(
        mode === "hard"
          ? `Revoked ${data.revoked} • deleted ${data.deleted}`
          : `Revoked ${data.revoked} demo accounts`
      );
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-base font-medium flex items-center gap-2">
              <FlaskConical className="h-4 w-4 text-primary" />
              Expired demo / test accounts
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Sandbox accounts created via influencer test codes whose access window has ended.
              Soft revoke removes their tier and à la carte grants. Hard delete also removes the
              auth user.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => run("soft")}
              disabled={busy || rows.length === 0}
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              Revoke grants ({rows.length})
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => run("hard")}
              disabled={busy || rows.length === 0}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              Revoke + delete ({rows.length})
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No expired demo accounts. Sandbox is clean.</p>
        ) : (
          <div className="rounded-md border divide-y">
            {rows.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 p-3 text-sm">
                <div className="min-w-0">
                  <div className="font-medium truncate">
                    {r.display_name || "Demo account"}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono truncate">
                    {r.id}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {r.tier && r.tier !== "tier_signal" && (
                    <Badge variant="secondary" className="text-[10px]">
                      {r.tier.replace("tier_", "")}
                    </Badge>
                  )}
                  {r.kyc_provider === "influencer_code" && (
                    <Badge variant="outline" className="text-[10px]">code-KYC</Badge>
                  )}
                  <Badge variant="destructive" className="text-[10px]">
                    <AlertTriangle className="h-2.5 w-2.5 mr-1" />
                    expired {new Date(r.demo_expires_at!).toLocaleDateString()}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default DemoAccountsPanel;
